import { useMemo, useState } from 'react'

import { resourceListTag, useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  EMPTY_PRODUCT_FILTER,
  OWN_CATALOG_CAPABILITIES,
  OWN_PRICE_TAG,
  OWN_WINDOWS,
  OwnPriceKindsScreen,
  ownChangePriceKind,
  ownCreatePriceKind,
  ownGroups,
  ownNomenclatureApi,
  ownPriceKinds,
  ownProducts,
  priceKindsVm,
  productsArgs,
  type FinderMode,
  type OwnPriceKindsActions,
  type PriceKindDraft,
} from '@krp/resource-own-ui'

import { useAppDispatch } from '../../app/hooks'
import { useOwnCapability } from './useOwnCapability'

const NEW_KIND: PriceKindDraft = { id: null, name: '', currency: 'RUB', archived: false, owner: 'own' }

/**
 * Binding вкладки «Цены» своей базы (RP-OWN3): виды цен и поиск товара по артикулу или штрихкоду целиком; цены товара —
 * отдельным окном. Черновик формы и набранный поиск — пока открыт экран.
 */
export function OwnPricesBinding() {
  const window = useWindow()
  const openWindow = useOpenWindow()
  const dispatch = useAppDispatch()
  const resourceId = window.params.rid ?? ''
  const canWrite = useOwnCapability(resourceId, OWN_CATALOG_CAPABILITIES.pricesWrite)
  const args = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const kinds = useQueryResult(ownPriceKinds, args)
  const groups = useQueryResult(ownGroups, args)

  const [draft, setDraft] = useState<PriceKindDraft | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [finder, setFinder] = useState<{ text: string; mode: FinderMode }>({ text: '', mode: 'sku' })
  const [searched, setSearched] = useState<{ text: string; mode: FinderMode } | null>(null)
  const [create, createState] = ownCreatePriceKind.useMutation()
  const [change, changeState] = ownChangePriceKind.useMutation()
  const command = draft?.id == null ? createState : changeState

  const findArgs = useMemo(() => {
    if (resourceId === '' || searched === null) return null
    const filter = { ...EMPTY_PRODUCT_FILTER, showArchived: true, ...(searched.mode === 'sku' ? { sku: searched.text } : { barcode: searched.text }) }
    return productsArgs(resourceId, filter, null)
  }, [resourceId, searched])
  const found = useQueryResult(ownProducts, findArgs)

  const vm = priceKindsVm({
    kinds,
    draft,
    command,
    done,
    canWrite,
    finder: { text: finder.text, mode: finder.mode, searched: searched !== null, page: findArgs === null ? null : found },
    groups: groups.data ?? [],
  })

  const actions = useMemo<OwnPriceKindsActions>(
    () => ({
      create: () => {
        createState.reset()
        setDone(null)
        setDraft(NEW_KIND)
      },
      edit: (typeId) => {
        const kind = kinds.data?.find((item) => item.id === typeId)
        if (kind === undefined) return
        changeState.reset()
        setDone(null)
        setDraft({ id: kind.id, name: kind.name, currency: kind.currency, archived: kind.archived, owner: kind.owner })
      },
      draftChanged: setDraft,
      closeForm: () => setDraft(null),
      save: () => {
        if (draft === null) return
        const name = draft.name.trim()
        const currency = draft.currency.trim()
        const kind = draft.id === null ? undefined : kinds.data?.find((item) => item.id === draft.id)
        const request =
          draft.id === null
            ? create({ resourceId, name, currency, owner: draft.owner })
            : kind === undefined
              ? null
              : change({ resourceId, typeId: draft.id, expectedVersion: kind.version, name, currency, archived: draft.archived, owner: draft.owner })
        if (request === null) return
        void request
          .unwrap()
          .then((saved) => {
            setDone(draft.id === null ? `Вид цены «${saved.name}» создан` : `Вид цены «${saved.name}» сохранён`)
            setDraft(null)
          })
          .catch(() => undefined)
      },
      reload: () => dispatch(ownNomenclatureApi.util.invalidateTags([resourceListTag(OWN_PRICE_TAG, resourceId)])),
      finderChanged: (text, mode) => setFinder({ text, mode }),
      find: () => {
        const text = finder.text.trim()
        if (text !== '') setSearched({ text, mode: finder.mode })
      },
      openPrices: (productId) => openWindow(OWN_WINDOWS.productPrices, { rid: resourceId, id: productId }),
      openProducts: () => openWindow(OWN_WINDOWS.products, { rid: resourceId }),
    }),
    [change, changeState, create, createState, dispatch, draft, finder, kinds.data, openWindow, resourceId],
  )

  return <OwnPriceKindsScreen vm={vm} actions={actions} />
}
